import { useContext } from "react";
import { useNavigate } from "react-router";
import { Check, FlipHorizontal, MessageSquareQuote } from "lucide-react";
import { UserContext } from "../../context/UserContext";
import Button from "../DesignSystem/Button";
import { Plans } from "./types";
import { LayoutContext } from "../../context/LayoutContext";

export default function PricingCards() {
    const { user } = useContext(UserContext);
    const { closePricingDialog } = useContext(LayoutContext);
    const navigate = useNavigate();

    const currentPlan = user!.plan.currentMarketingName as Plans;

    let professionalButtonText = "Upgrade to Professional";

    if (currentPlan === Plans.Professional) {
        professionalButtonText = "Current Plan";
    } else if (currentPlan === Plans.Lite) {
        professionalButtonText = "Upgrade to Professional";
    } else if (currentPlan === Plans.ProfessionalTrial) {
        professionalButtonText = "Keep Professional";
    }

    return (
        <div>
            <div className="mb-4 text-center text-2xl font-bold">Flexible pricing for every buyer.</div>
            <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div className="relative rounded-xl border border-stone-200 px-4 py-6">
                    <div className="mb-4 flex gap-4">
                        <div>
                            <div className="mb-1 text-2xl font-semibold">Lite</div>
                            <div>The plan for individual purchasers.</div>
                        </div>
                        <div className="text-3xl font-semibold">
                            <span>$99</span>
                            <span className="text-base font-normal">/mo</span>
                        </div>
                    </div>
                    <div className="h-px w-full bg-stone-200" />
                    <div className="my-4 grid grid-cols-[auto_1fr] items-center gap-2.5">
                        <MessageSquareQuote className="h-4 w-4 stroke-stone-700" />
                        <div>
                            Send & Manage <span className="underline">10 RFQs</span>
                        </div>
                        <FlipHorizontal className="h-4 w-4 stroke-stone-700" />
                        <div>
                            Analyze <span className="underline">Unlimited Quotes</span>
                        </div>
                    </div>
                    <div className="mb-4 h-px w-full bg-stone-200" />

                    <div className="mb-12 grid grid-cols-[auto_1fr] gap-x-2.5 gap-y-3">
                        <div className="p-1">
                            <Check className="stroke-bronze-600 h-4 w-4" />
                        </div>
                        <div>Out of the box solution. Ready to source in 5 minutes.</div>
                        <div className="p-1">
                            <Check className="stroke-bronze-600 h-4 w-4" />
                        </div>
                        <div>Integrates with your work email. No vendor set up.</div>
                        <div className="p-1">
                            <Check className="stroke-bronze-600 h-4 w-4" />
                        </div>
                        <div>Upgrade your plan at any time if you hit your usage limits.</div>
                    </div>
                    {(currentPlan === Plans.Lite ||
                        currentPlan === Plans.Starter ||
                        currentPlan === Plans.Trial ||
                        currentPlan === Plans.ProfessionalTrial) && (
                        <Button
                            text={currentPlan === Plans.Lite ? "Current Plan" : "Upgrade to Lite"}
                            disabled={currentPlan === Plans.Lite}
                            color="white"
                            className="absolute bottom-6 left-3 w-[calc(100%-24px)] justify-center"
                            onClick={() => {
                                closePricingDialog();
                                navigate(`/checkout?plan=${Plans.Lite}`);
                            }}
                            icon={<img src="https://app.purchaser.ai/images/icons/upgrade.svg" className="h-4 w-4" />}
                        />
                    )}
                </div>
                <div className="relative rounded-xl border border-blue-200 bg-linear-to-r from-white to-blue-50 px-4 py-6">
                    <div className="mb-4 flex gap-4">
                        <div>
                            <div className="mb-1 text-2xl font-semibold">Professional</div>
                            <div>The plan for scaling teams.</div>
                        </div>
                        <div className="text-base font-normal">
                            <span className="text-3xl font-semibold">$299</span>
                            <span>/mo</span>
                            <div className="text-right">per seat</div>
                        </div>
                    </div>
                    <div className="h-px w-full bg-stone-200" />
                    <div className="my-4 grid grid-cols-[auto_1fr] items-center gap-2.5">
                        <MessageSquareQuote className="h-4 w-4 stroke-stone-700" />
                        <div>
                            Manage <span className="underline">Unlimited RFQs</span>
                        </div>
                        <FlipHorizontal className="h-4 w-4 stroke-stone-700" />
                        <div>
                            Analyze <span className="underline">Unlimited Quotes</span>
                        </div>
                    </div>
                    <div className="mb-4 h-px w-full bg-stone-200" />
                    <div className="grid grid-cols-[auto_1fr] gap-x-2.5 gap-y-3">
                        <div className="p-1">
                            <Check className="stroke-bronze-600 h-4 w-4" />
                        </div>
                        <div>Centralize your sourcing data across your entire team.</div>
                        <div className="p-1">
                            <Check className="stroke-bronze-600 h-4 w-4" />
                        </div>
                        <div>Scales with the size of your team and sourcing activity.</div>
                    </div>
                    <Button
                        text={professionalButtonText}
                        disabled={currentPlan === Plans.Professional}
                        color="blueGradient"
                        className="absolute bottom-6 left-3 w-[calc(100%-24px)] justify-center"
                        onClick={() => {
                            closePricingDialog();
                            navigate(`/checkout?plan=${Plans.Professional}`);
                        }}
                        icon={<img src="https://app.purchaser.ai/images/icons/upgrade-light.svg" className="h-4 w-4" />}
                    />
                </div>
                <div className="relative rounded-xl border border-stone-200 px-4 py-6 sm:col-span-2 md:col-span-1 lg:col-span-1">
                    <div className="mb-4 h-[84px]">
                        <div className="mb-1 text-2xl font-semibold">Enterprise</div>
                        <div>The plan for larger teams.</div>
                    </div>
                    <div className="mb-4 h-px w-full bg-stone-200" />
                    <div className="mb-12">
                        Our custom plan is built for teams larger than 5, offering seat-based discounts to fit your budget and a platform that works
                        with your existing technology.
                    </div>
                    <Button
                        text="Contact Us"
                        color="white"
                        className="absolute bottom-6 left-3 w-[calc(100%-24px)] justify-center"
                        href="mailto:founders@purchaser.ai"
                    />
                </div>
            </div>
            <div className="text-center">
                <span className="mr-3">Learn how we can make sourcing more efficient and your business more profitable.</span>
                <a target="_blank" className="text-bronze-600" href="https://purchaser.ai/book-a-demo">
                    Talk to Someone
                </a>
            </div>
        </div>
    );
}
